/**
 * Organization API client (Phase 1 / P4 — Frontend OrgSwitcher).
 *
 * Wraps the endpoints exposed by ``backend/app/api/organizations.py``:
 *  - GET    /api/organizations            — list caller memberships
 *  - POST   /api/organizations            — create (caller becomes owner)
 *  - GET    /api/organizations/current    — active org id + memberships
 *  - PUT    /api/organizations/current    — switch active org (new token)
 *  - GET    /api/organizations/{id}       — detail
 *  - PATCH  /api/organizations/{id}       — admin/owner update
 *  - DELETE /api/organizations/{id}       — owner-only archive (Phase 2 / T2.1)
 *  - GET    /api/organizations/{id}/members
 *  - POST   /api/organizations/{id}/members
 *  - PATCH  /api/organizations/{id}/members/{email}
 *  - DELETE /api/organizations/{id}/members/{email}
 *  - POST   /api/organizations/{id}/break-glass
 *  - GET    /api/organizations/{id}/break-glass
 *
 * Phase 2 will add invite token + domain auto-join (already shipped in T1.4).
 */
import { api } from './api'

// --------------------------------------------------------------------------
// Response types — mirrors organization_schemas.py camelCase shape.
// --------------------------------------------------------------------------
export type OrgRole = 'owner' | 'admin' | 'member' | 'guest'
export type OrgType = 'personal' | 'team' | 'demo' | 'system'
export type OrgState = 'active' | 'suspended' | 'archived'

export interface OrgSummary {
  id: string
  slug: string
  displayName: string
  orgType: OrgType
  state: OrgState
  planTier: string
  billingExempt: boolean
  role?: OrgRole | null
  createdAt?: string | null
}

export interface OrgDetail extends OrgSummary {
  description?: string | null
  ownerEmail: string
  settings: Record<string, unknown>
  updatedAt: string
  callerRole?: OrgRole | null
  memberCount: number
}

export interface OrgMemberDetail {
  email: string
  role: OrgRole
  state: string
  invitedBy?: string | null
  joinedAt: string
  lastSeenAt?: string | null
}

export interface OrgMembersResponse {
  items: OrgMemberDetail[]
  nextCursor?: string | null
}

export interface OrgListResponse {
  items: OrgSummary[]
  total: number
}

export interface CurrentOrgResponse {
  activeOrgId: string | null
  orgs: OrgSummary[]
}

export interface OrgSwitchResponse {
  activeOrgId: string
  accessToken: string
  tokenType: string
  expiresIn: number
}

export interface OrgCreateRequest {
  displayName: string
  slug?: string
  description?: string
  plan?: string
  orgType?: OrgType
  defaultDomain?: string
  settings?: Record<string, unknown>
}

export interface OrgUpdateRequest {
  displayName?: string
  description?: string
  plan?: string
  settings?: Record<string, unknown>
}

export interface OrgBreakGlassStartRequest {
  targetProjectId: string
  reason?: string
  ttlMinutes?: number
}

export interface OrgBreakGlassSession {
  id: number
  organizationId: string
  adminEmail: string
  targetProjectId: string
  startedAt: string
  expiresAt: string
  reason?: string | null
  auditLoggedAt?: string | null
}

// Phase 2 / T3 — Billing org-scoping ----------------------------------------
export type OrgBillingExemptSource = 'column' | 'env' | null
export type OrgSubscriptionScope = 'org' | 'project' | 'none'

export interface OrgBillingSummary {
  organizationId: string
  planTier: string
  billingExempt: boolean
  billingExemptSource: OrgBillingExemptSource
  projectCount: number
  projectQuota: number | null
  activeMemberCount: number
  userQuota: number | null
  subscriptionSource: OrgSubscriptionScope
  legacyOwnerEmail?: string | null
}

export interface OrgBillingPatchRequest {
  planTier?: string
  billingExempt?: boolean
}

// --------------------------------------------------------------------------
// API client
// --------------------------------------------------------------------------
export const organizationsAPI = {
  list: async (orgType?: OrgType): Promise<OrgListResponse> => {
    const params = orgType ? { type: orgType } : undefined
    const { data } = await api.get<OrgListResponse>('/api/organizations', {
      params,
    })
    return data
  },

  create: async (payload: OrgCreateRequest): Promise<OrgDetail> => {
    const { data } = await api.post<OrgDetail>('/api/organizations', payload)
    return data
  },

  get: async (orgId: string): Promise<OrgDetail> => {
    const { data } = await api.get<OrgDetail>(
      `/api/organizations/${encodeURIComponent(orgId)}`
    )
    return data
  },

  update: async (
    orgId: string,
    payload: OrgUpdateRequest
  ): Promise<OrgDetail> => {
    const { data } = await api.patch<OrgDetail>(
      `/api/organizations/${encodeURIComponent(orgId)}`,
      payload
    )
    return data
  },

  /**
   * Archive (soft-delete) an organization.
   *
   * Phase 2 / T2.1. Owner-only (admins are rejected). The backend default
   * is ``state='archived'`` (members preserved). Pass ``confirmSlug`` (the
   * org's slug) via the ``X-Confirm-Org-Delete`` header to bypass the
   * "multiple active members" guard (the backend treats the matching
   * header as the explicit force signal). Pass ``hard=true`` (global admin
   * only) to request a true cascade delete.
   *
   * The backend responds with ``OrgDeleteResponse`` JSON; we surface it as
   * ``OrgDetail | null`` to keep the call site flexible.
   */
  deleteOrg: async (
    orgId: string,
    opts: { hard?: boolean; confirmSlug?: string } = {}
  ): Promise<OrgDetail | null> => {
    const params: Record<string, string> = {}
    if (opts.hard) params.hard = 'true'
    const headers: Record<string, string> = {}
    if (opts.confirmSlug) headers['X-Confirm-Org-Delete'] = opts.confirmSlug
    const res = await api.delete<OrgDetail | null>(
      `/api/organizations/${encodeURIComponent(orgId)}`,
      { params, headers }
    )
    if (res.status === 204) return null
    return (res.data as OrgDetail) ?? null
  },

  getCurrent: async (): Promise<CurrentOrgResponse> => {
    const { data } = await api.get<CurrentOrgResponse>(
      '/api/organizations/current'
    )
    return data
  },

  switchCurrent: async (orgId: string): Promise<OrgSwitchResponse> => {
    const { data } = await api.put<OrgSwitchResponse>(
      '/api/organizations/current',
      { orgId }
    )
    return data
  },

  listMembers: async (
    orgId: string,
    opts: { limit?: number; cursor?: string } = {}
  ): Promise<OrgMembersResponse> => {
    const { data } = await api.get<OrgMembersResponse>(
      `/api/organizations/${encodeURIComponent(orgId)}/members`,
      { params: { limit: opts.limit, cursor: opts.cursor } }
    )
    return data
  },

  addMember: async (
    orgId: string,
    payload: { email: string; role?: OrgRole }
  ): Promise<OrgMemberDetail> => {
    const { data } = await api.post<OrgMemberDetail>(
      `/api/organizations/${encodeURIComponent(orgId)}/members`,
      payload
    )
    return data
  },

  patchMember: async (
    orgId: string,
    email: string,
    role: OrgRole
  ): Promise<OrgMemberDetail> => {
    const { data } = await api.patch<OrgMemberDetail>(
      `/api/organizations/${encodeURIComponent(orgId)}/members/${encodeURIComponent(email)}`,
      { role }
    )
    return data
  },

  removeMember: async (orgId: string, email: string): Promise<void> => {
    await api.delete(
      `/api/organizations/${encodeURIComponent(orgId)}/members/${encodeURIComponent(email)}`
    )
  },

  startBreakGlass: async (
    orgId: string,
    payload: OrgBreakGlassStartRequest
  ): Promise<OrgBreakGlassSession> => {
    const { data } = await api.post<OrgBreakGlassSession>(
      `/api/organizations/${encodeURIComponent(orgId)}/break-glass`,
      payload
    )
    return data
  },

  listBreakGlass: async (
    orgId: string,
    mine?: boolean
  ): Promise<{ items: OrgBreakGlassSession[] }> => {
    const { data } = await api.get<{ items: OrgBreakGlassSession[] }>(
      `/api/organizations/${encodeURIComponent(orgId)}/break-glass`,
      { params: { mine: mine ? 'true' : undefined } }
    )
    return data
  },

  // Phase 2 / T3 — Billing org-scoping --------------------------------------
  getBilling: async (orgId: string): Promise<OrgBillingSummary> => {
    const { data } = await api.get<OrgBillingSummary>(
      `/api/organizations/${encodeURIComponent(orgId)}/billing`
    )
    return data
  },

  patchBilling: async (
    orgId: string,
    payload: OrgBillingPatchRequest
  ): Promise<OrgBillingSummary> => {
    const { data } = await api.patch<OrgBillingSummary>(
      `/api/organizations/${encodeURIComponent(orgId)}/billing`,
      payload
    )
    return data
  },
}

export default organizationsAPI
